// Eskom PARCC — the shared, standardised filter model.
//
// One source of truth for the nine filter dimensions the project owner specified
// in feedback/Filters.xlsx, reused by Dashboard, Transactions and Allocations so
// the three pages can never drift apart. Each dimension knows how to read its
// (canonical) value off a transaction, how to match a selection, and what its
// dropdown options are. Raw register values are folded onto the controlled
// vocabulary via normalizeFacets so the filters actually match real data.
//
// Compliance is supplied per-row by the caller (Transactions pre-computes its
// effective compliance), so this stays free of domain maths — mirroring the
// existing filterTransactions contract.
//
// Pure, no React. Unit-tested in test/domain/filters.test.ts.
import type { Transaction } from '../data/types';
import { GAP_STATUS_OPTIONS, FILTER_COMPLIANCE_OPTIONS } from './enums';
import { normalizeGapStatus } from './normalize';
import {
  FILTER_DATASET_OPTIONS,
  FILTER_PERIOD_OPTIONS,
  FILTER_MECHANISM_OPTIONS,
  FILTER_DIVISION_OPTIONS,
  normalizeDatasetFacet,
  normalizeMechanismFacet,
  normalizeDivisionFacet,
  yesNo,
} from './normalizeFacets';

export type FacetKey =
  | 'dataset'
  | 'period'
  | 'reviewed'
  | 'panel'
  | 'gapsStatus'
  | 'compliance'
  | 'mechanism'
  | 'division';

/** The selected value per facet ('' = no selection / "all"). */
export type FacetState = Record<FacetKey, string>;

/** A transaction plus its (optional) pre-computed effective compliance level. */
export interface FacetRow {
  tx: Transaction;
  compliance?: string;
}

export const EMPTY_FACETS: FacetState = {
  dataset: '',
  period: '',
  reviewed: '',
  panel: '',
  gapsStatus: '',
  compliance: '',
  mechanism: '',
  division: '',
};

const YES_NO = ['Yes', 'No'] as const;

/** Reviewed = the explicit flag OR a review status of "Reviewed" (mirrors editTransaction.isReviewed). */
function isReviewed(tx: Transaction): boolean {
  if (tx.reviewed === true) return true;
  return String(tx.reviewStatus ?? '').trim().toLowerCase() === 'reviewed';
}

/** Per-facet accessor → the canonical value used for both matching and aggregation. */
const ACCESSORS: Record<FacetKey, (row: FacetRow) => string> = {
  dataset: (r) => normalizeDatasetFacet(r.tx.dataset),
  period: (r) => String(r.tx.period ?? '').trim(),
  reviewed: (r) => yesNo(isReviewed(r.tx)),
  panel: (r) => yesNo(r.tx.panel),
  // Normalised so legacy rows (Unresolved / Resolved / Self-declared) match
  // the four-state filter options without a data migration.
  gapsStatus: (r) => normalizeGapStatus(r.tx.gapsStatus),
  compliance: (r) => String(r.compliance ?? r.tx.complianceLevel ?? '').trim(),
  mechanism: (r) => normalizeMechanismFacet(r.tx.mechanism),
  division: (r) => normalizeDivisionFacet(r.tx.division),
};

/** The canonical value of one facet for a row. */
export function facetValue(row: FacetRow, key: FacetKey): string {
  return ACCESSORS[key](row);
}

/** True when the row satisfies every selected (non-empty) facet (AND semantics). */
export function matchesFacets(row: FacetRow, state: FacetState): boolean {
  return (Object.keys(state) as FacetKey[]).every((key) => {
    const selected = state[key];
    return !selected || facetValue(row, key) === selected;
  });
}

/** Lowercased free-text haystack across the searchable fields (mirrors filterTransactions). */
export function facetHaystack(tx: Transaction): string {
  return [
    tx.referenceNo,
    tx.baseNo,
    tx.recordLabel,
    tx.supplier,
    tx.mechanism,
    tx.dataset,
    tx.division,
    tx.period,
    tx.year,
    tx.csdNumber,
    tx.valueDisplay,
    tx.link,
    tx.assignedToEmail,
  ]
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
    .join(' ')
    .toLowerCase();
}

/** The fixed (controlled-vocabulary) option list per facet, in owner-facing order. */
const FIXED_OPTIONS: Record<FacetKey, readonly string[]> = {
  dataset: FILTER_DATASET_OPTIONS,
  period: FILTER_PERIOD_OPTIONS,
  reviewed: YES_NO,
  panel: YES_NO,
  gapsStatus: GAP_STATUS_OPTIONS,
  compliance: FILTER_COMPLIANCE_OPTIONS,
  mechanism: FILTER_MECHANISM_OPTIONS,
  division: FILTER_DIVISION_OPTIONS,
};

/** The nine facet definitions (key + label + fixed options) for building the filter UI. */
export const FACET_DEFS: { key: FacetKey; label: string; options: readonly string[] }[] = [
  { key: 'dataset', label: 'Dataset', options: FIXED_OPTIONS.dataset },
  { key: 'period', label: 'Period', options: FIXED_OPTIONS.period },
  { key: 'reviewed', label: 'Reviewed', options: FIXED_OPTIONS.reviewed },
  { key: 'panel', label: 'Panel', options: FIXED_OPTIONS.panel },
  { key: 'gapsStatus', label: 'Gaps Status', options: FIXED_OPTIONS.gapsStatus },
  { key: 'compliance', label: 'Compliance Level', options: FIXED_OPTIONS.compliance },
  { key: 'mechanism', label: 'Mechanism', options: FIXED_OPTIONS.mechanism },
  { key: 'division', label: 'Division', options: FIXED_OPTIONS.division },
];

/**
 * Hybrid options for a facet: the full controlled vocabulary (in spec order)
 * followed by any genuine outlier values present in the data (canonicalised,
 * de-duplicated, sorted) — so the dropdown always shows the owner's vocabulary
 * yet never hides a real value the data carries.
 */
export function buildFacetOptions(rows: readonly FacetRow[], key: FacetKey): string[] {
  const fixed = FIXED_OPTIONS[key];
  const present = new Set<string>();
  for (const row of rows) {
    const v = facetValue(row, key);
    if (v) present.add(v);
  }
  const outliers = [...present]
    .filter((v) => !fixed.includes(v))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  return [...fixed, ...outliers];
}
